// Features: MCRO001-F0027, MCRO001-F0029 (inherits MCRO001-F0007/F0008/F0021)
// Tests: T-MCRO-027, T-MCRO-029
// MCRO001-F0085 / T-MCRO-085 (rail line presence, choreo issue #39): a served
// approved saved map renders its rail line regardless of the video content
// fetch's outcome — an unconfirmed rendition is an honest `degraded` state
// ('media-loading' | 'media-unavailable'), never a silent drop of the map.
// Only durable authority truth (identity, revision, approval, saved state)
// still fails closed, and a record whose timing map fails hard validation is
// surfaced as an error state (readServedInvalidDemonstrations), not dropped.

import {
  projectCountToMediaMs,
  projectMediaMsToCount,
  validateDemonstrationTimingMap,
} from './demonstrationTimingMap';

const identifier = (value) => typeof value === 'string' ? value.trim() : '';
const finite = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;

function approvedCandidate(value, revisionId) {
  const demonstrationId = identifier(value?.demonstrationId);
  const sequenceLabel = identifier(value?.sequenceLabel);
  const rendition = value?.rendition;
  // T-MCRO-085: identity, revision and authorization gates remain hard
  // fail-closed refusals — a record failing them is not a served approved
  // saved map for this actor/revision (durable truth, correctly rendered as
  // nothing). Everything rendition-related below degrades honestly instead.
  if (!demonstrationId || !sequenceLabel || value?.eligible !== true || value?.mapState !== 'saved'
      || value?.authorizationState !== 'approved' || value?.revisionId !== revisionId) return null;
  // A rendition RECORD that is present but not approved is durable
  // authorization truth about the media and stays hard-refused. The entire
  // absence of a rendition record is a content-reference outcome (no media
  // claim to authorize, and none will be played) and degrades honestly below.
  if (rendition && rendition.authorizationState !== 'approved') return null;
  try {
    const timingMap = validateDemonstrationTimingMap(value.timingMap, revisionId);
    // T-MCRO-085 case A: the checks below are MEDIA-reference outcomes, not
    // map outcomes. A slow, failed or not-yet-started content fetch (or a
    // reference that does not provably match this map's authored rendition)
    // makes the MEDIA unusable for projection — it must never delete the
    // served map, which is exactly the silent absence the owner reported as
    // #39. The projection marks the honest degradation state instead:
    //   'media-loading'      — a fetch is in flight and nothing else conflicts
    //   'media-unavailable'  — the fetch failed/emptied, the rendition
    //                          reference is missing/mismatched, or the map
    //                          was authored against a different rendition
    //                          version than the one served
    const versionMismatch = value?.expectedRenditionVersion !== undefined
      && value.expectedRenditionVersion !== rendition?.version;
    const referenceMismatch = identifier(timingMap.renditionId) !== identifier(rendition?.renditionId);
    const mediaConfirmed = rendition?.availability === 'available' && !versionMismatch && !referenceMismatch
      && Boolean(identifier(rendition?.renditionId)) && Boolean(identifier(rendition?.source));
    const degraded = mediaConfirmed ? undefined
      : !referenceMismatch && !versionMismatch && rendition?.availability === 'loading'
        ? 'media-loading'
        : 'media-unavailable';
    return {
      demonstrationId,
      authorityEligible: true,
      sequenceLabel,
      revisionId,
      priority: finite(value?.priority) ?? undefined,
      colour: identifier(value?.colour),
      canEditTiming: value?.canEditTiming === true,
      expectedMapVersion: Number.isInteger(value?.expectedMapVersion)
        && value.expectedMapVersion >= 0 ? value.expectedMapVersion : undefined,
      mediaId: identifier(value?.mediaId),
      relation: value?.relation && typeof value.relation === 'object' ? { ...value.relation } : undefined,
      renditionMutation: value?.renditionMutation && typeof value.renditionMutation === 'object'
        ? { ...value.renditionMutation }
        : undefined,
      timingMap,
      ...(degraded ? { degraded } : {}),
      rendition: rendition ? {
        renditionId: identifier(rendition.renditionId),
        version: rendition.version,
        source: identifier(rendition.source),
      } : undefined,
    };
  } catch (_) {
    // T-MCRO-085: a served record whose timing map fails hard validation
    // (validateDemonstrationTimingMap) is honestly INVALID, not absent. It is
    // surfaced as an error-state entry — rendered by the rail as an error
    // line via readServedInvalidDemonstrations — instead of being silently
    // dropped here. readApprovedMappedDemonstrations never returns these (the
    // projection math downstream requires a valid map).
    return {
      demonstrationId,
      sequenceLabel,
      revisionId,
      errorState: 'invalid-map',
      priority: finite(value?.priority) ?? undefined,
      colour: identifier(value?.colour),
      canEditTiming: value?.canEditTiming === true,
      expectedMapVersion: Number.isInteger(value?.expectedMapVersion)
        && value.expectedMapVersion >= 0 ? value.expectedMapVersion : undefined,
      mediaId: identifier(value?.mediaId),
    };
  }
}

/** Single synchronous read of the injected boundary; fails closed. */
function suppliedCandidates(boundary, context) {
  if (!boundary || typeof boundary.readApprovedSavedDemonstrations !== 'function') return [];
  try {
    const supplied = boundary.readApprovedSavedDemonstrations({
      organisationId: context?.organisationId,
      routineId: context?.routineId,
      revisionId: context?.revisionId,
    });
    return Array.isArray(supplied) ? supplied : [];
  } catch (_) {
    return [];
  }
}

/**
 * Dependency-safe adapter boundary. The future #920 integration may inject a synchronous
 * approved projection here; absence, asynchronous values, exceptions and malformed values
 * all fail closed without inventing an endpoint or wire schema.
 *
 * T-MCRO-085: records that ARE served, approved and saved but whose video is
 * loading/unavailable are returned as ordinary candidates carrying an honest
 * `degraded` state — line presence never depends on the content fetch. Records
 * whose timing map fails hard validation are excluded here and surfaced by
 * readServedInvalidDemonstrations instead.
 */
export function readApprovedMappedDemonstrations(boundary, context) {
  const seen = new Set();
  return suppliedCandidates(boundary, context).reduce((accepted, item) => {
    const candidate = approvedCandidate(item, context?.revisionId);
    if (!candidate || candidate.errorState !== undefined || seen.has(candidate.demonstrationId)) return accepted;
    seen.add(candidate.demonstrationId);
    accepted.push(candidate);
    return accepted;
  }, []);
}

/**
 * T-MCRO-085: the paired error read for readApprovedMappedDemonstrations —
 * served approved saved records whose timing map failed hard validation.
 * Honest invalidity, never a silent drop; empty when nothing served is invalid.
 */
export function readServedInvalidDemonstrations(boundary, context) {
  const seen = new Set();
  return suppliedCandidates(boundary, context).reduce((accepted, item) => {
    const candidate = approvedCandidate(item, context?.revisionId);
    if (!candidate?.errorState || seen.has(candidate.demonstrationId)) return accepted;
    seen.add(candidate.demonstrationId);
    accepted.push(candidate);
    return accepted;
  }, []);
}

export function mappedDemonstrationAuthorityKey(candidate) {
  if (!candidate?.demonstrationId || !candidate?.timingMap) return '';
  return JSON.stringify({
    demonstrationId: candidate.demonstrationId,
    revisionId: candidate.revisionId,
    expectedMapVersion: candidate.expectedMapVersion ?? null,
    renditionId: candidate.rendition?.renditionId,
    renditionVersion: candidate.rendition?.version,
    timingMap: candidate.timingMap,
  });
}

export function activeMappedSegment(timingMap, count, revisionId) {
  const map = validateDemonstrationTimingMap(timingMap, revisionId);
  const target = finite(count);
  if (target === null || target < map.startCount || target > map.endCount) return null;
  const points = map.syncPoints;
  if (target === map.endCount) return { left: points.at(-2), right: points.at(-1), index: points.length - 2 };
  const rightIndex = points.findIndex((point) => point.count > target);
  if (rightIndex < 1) return null;
  return { left: points[rightIndex - 1], right: points[rightIndex], index: rightIndex - 1 };
}

export function projectMappedDemonstration({
  timingMap,
  count,
  bpm,
  rehearsalRate,
  revisionId,
  allowExplicitFocusRate = false,
}) {
  const map = validateDemonstrationTimingMap(timingMap, revisionId);
  const target = finite(count);
  if (target === null || target < map.startCount || target > map.endCount) {
    return { visible: false, map };
  }
  const segment = activeMappedSegment(map, target, revisionId);
  const beatsPerMinute = finite(bpm);
  const rate = finite(rehearsalRate);
  // F0029 focus-on activation may deliberately set an explicit positive session
  // rate outside the ordinary F0008 0.50–1.50 control range to render the mapped
  // video at exactly 1x. The standard input bounds remain unchanged.
  const validRate = rate !== null && (allowExplicitFocusRate
    ? rate > 0
    : rate >= 0.5 && rate <= 1.5);
  if (!segment || beatsPerMinute === null || beatsPerMinute <= 0 || !validRate) {
    return { visible: false, map };
  }
  const segmentMediaDurationMs = segment.right.mediaMs - segment.left.mediaMs;
  const segmentRehearsalDurationAt100Ms = (segment.right.count - segment.left.count) * (60000 / beatsPerMinute);
  const segmentBaseVideoRate = segmentMediaDurationMs / segmentRehearsalDurationAt100Ms;
  const mediaMs = projectCountToMediaMs(map, target, revisionId);
  return {
    visible: true,
    map,
    mediaMs,
    count: projectMediaMsToCount(map, mediaMs, revisionId),
    segmentIndex: segment.index,
    segmentBaseVideoRate,
    effectiveVideoRate: segmentBaseVideoRate * rate,
    atInclusiveEnd: target === map.endCount,
  };
}
